import { observer } from "mobx-react"; // Import MobX observer
import React, { useCallback, useMemo } from "react"; // Import necessary React hooks and components
import { useHistory } from "react-router-dom"; // Import useHistory for navigation
import demoStoremaleIcon from "../../../../assets/Images/man.png"; // Import male icon
import demoStoreallIcon from "../../../../assets/Images/teamwork.png"; // Import all staff icon
import demoStorefemaleIcon from "../../../../assets/Images/woman.png"; // Import female icon
import { getEventWithMostOverlaps } from "../../../../constants/constants"; // Import utility function to get event with most overlaps
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enumeration
import { BookingTypeEnum } from "../../../../constants/enums/booking-type-enum"; // Import booking type enumeration
import { formatMessage } from "../../../../translations/format-message"; // Import message formatting function
import DraggableEvent from "../draggableEvent"; // Import DraggableEvent component
import AddBreakDetailsPopupHOC from "../hoc/add-break-details-popup-hoc"; // Import HOC for adding break details
import AllMoreEventsPopupHOC from "../hoc/all-more-events-popup-hoc"; // Import HOC for all more events popup
import ReservationDetailsPopupHOC from "../hoc/reservation-details-popup-hoc"; // Import HOC for reservation details popup
import AllMoreEventsComponent from "../modal-components/all-more-events-component"; // Import all more events modal component
import ReservationDetailsComponent from "../modal-components/reservation-details-component"; // Import reservation details modal component
import EventCSSProperties from "./event-css-properties"; // Import function for event CSS properties
import IntervalEvent from "./interval-event-component"; // Import interval event component
import ServiceNameDisplayComponent from "./services-name-component"; // Import service name display component

// Define interface for component props
interface IProps {
  key: string; // Unique key for the component
  day: any; // Day object
  currentDate: any; // Current date
  dataObject: any; // Data object containing event details
  timeLabel: any; // Time label for the event
  start: any; // Start time of the event
  end: any; // End time of the event
  overlappingCount: number; // Count of overlapping events
  defaultMargin: number; // Default margin for event display
  allEvents: any[]; // Array of all events
  allBreaks: any[]; // Array of all breaks
  events: any[]; // Array of current events
  cellWidth: number; // Width of the cell in the calendar
  storeId: number; // ID of the store
  viewOnly: boolean; // Flag for view-only mode
  changeView?: (date: string) => void; // Optional function to change view
  breaksCount?: number; // Optional count of breaks
  allMatchedBreaks?: any[]; // Optional array of matched breaks
  index?: number; // Optional index of the event
  staffList?: any[]; // Optional list of staff
  showReservationDetailsPopup?: boolean; // Flag to show reservation details popup
  reservationDetailsToggleHandler?: () => void; // Optional function to toggle reservation details popup
  allMoreEventsPopup?: boolean; // Flag to show all more events popup
  allMoreEventsToggleHandler?: () => void; // Optional function to toggle all more events popup
}

// Define functional component
const InProgressEvent: React.FC<IProps> = (props) => {
  const {
    key,
    day,
    currentDate,
    dataObject,
    timeLabel,
    start,
    end,
    overlappingCount,
    defaultMargin,
    events,
    allEvents,
    allBreaks,
    cellWidth,
    storeId,
    viewOnly,
    changeView,
    breaksCount,
    index,
    staffList,
    showReservationDetailsPopup,
    reservationDetailsToggleHandler,
    allMoreEventsPopup,
    allMoreEventsToggleHandler,
    allMatchedBreaks,
  } = props; // Destructure props

  const history = useHistory(); // Initialize history for navigation

  // Calculate the number of appointments based on overlaps
  const appointmentsCounts = useMemo(() => {
    const eventWithMost = getEventWithMostOverlaps(dataObject, allEvents); // Get event with most overlaps
    return eventWithMost
      ? `${eventWithMost.OverlappedEventsIds?.length - 2}` // Return count of overlapped events
      : `${dataObject?.Overlapped - 2}`; // Return count from dataObject
  }, [dataObject, allEvents, dataObject?.Overlapped]); // Dependencies for memoization

  // Determine if breaks should be shown
  const shouldShowBreaks = useMemo(
    () => (breaksCount && breaksCount > 0 ? true : false), // Check if breaks count is greater than 0
    [breaksCount] // Dependency for memoization
  );

  // Determine staff icon based on preference
  const staffIcon = useMemo(() => {
    if (dataObject?.StaffPreference === 1) return demoStoremaleIcon; // Male icon
    if (dataObject?.StaffPreference === 2) return demoStorefemaleIcon; // Female icon
    return demoStoreallIcon; // All staff icon
  }, [dataObject?.StaffPreference]); // Dependency for memoization

  // Handler to open reservation details popup
  const openReservationDetailsPopup = useCallback(() => {
    reservationDetailsToggleHandler?.(); // Call toggle handler if defined
  }, [reservationDetailsToggleHandler]); // Dependency for callback

  // Handler to open all more events popup
  const openAllMoreEventsPopup = useCallback(() => {
    allMoreEventsToggleHandler?.(); // Call toggle handler if defined
  }, [allMoreEventsToggleHandler]); // Dependency for callback

  // Calculate draggable event style
  const draggableStyle = useMemo(
    () =>
      EventCSSProperties(
        start,
        end,
        overlappingCount * defaultMargin,
        cellWidth * (dataObject?.TotalTime / 5)
      ), // Calculate style based on event properties
    [
      start,
      end,
      overlappingCount,
      defaultMargin,
      cellWidth,
      dataObject?.TotalTime,
    ] // Dependencies for memoization
  );

  return (
    <>
      {/* All More Events Modal */}
      {allMoreEventsPopup && allMoreEventsToggleHandler && (
        <AllMoreEventsComponent
          onModalClosed={allMoreEventsToggleHandler} // Handler for modal close
          matchedEvent={dataObject} // Pass matched event data
          AllEvents={allEvents} // Pass all events
          viewOnly={viewOnly} // Pass view-only flag
          storeId={storeId} // Pass store ID
          day={day} // Pass day
          staffList={staffList} // Pass staff list
          changeView={changeView} // Pass change view function
          breaks={allMatchedBreaks} // Pass matched breaks
        />
      )}

      {/* Reservation Details Modal */}
      {showReservationDetailsPopup && reservationDetailsToggleHandler && (
        <ReservationDetailsComponent
          onModalClosed={reservationDetailsToggleHandler} // Handler for modal close
          event={dataObject} // Pass event data
          storeId={storeId} // Pass store ID
          viewOnly={viewOnly} // Pass view-only flag
        />
      )}

      <DraggableEvent
        key={key} // Unique key for the draggable event
        currentDate={currentDate} // Pass current date
        area="Inner" // Define area for draggable event
        data={dataObject} // Pass event data
        style={draggableStyle} // Pass calculated style
      >
        {({ onResizingChange }) => ( // Render function for draggable event
          <>
            <div
              className={`event ${
                dataObject?.BookingType === BookingTypeEnum.NewWeb
                  ? "new-upcoming-event"
                  : "upcoming-event"
              }`}
              data-label={`INP-RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${dataObject?.Id}`} // Data label for accessibility
            >
              <div onClick={openReservationDetailsPopup} style={{ cursor: "pointer" }}>
                <div>{dataObject?.CustomerName}</div>
                <div>{dataObject?.StartTime} - {dataObject?.EndTime}</div>
                {/* BC: {props.breaksCount} OBR: {dataObject?.OverlappedBreaks?.length} SSB : {JSON.stringify(shouldShowBreaks)} */}
                {/* BRK: {JSON.stringify(props.allBreaks)} */}
                <div className="complete-event-details">
                  {dataObject?.AppointmentStatus === bookingStatusEnum.Complete && (
                    <span className="badge bg-success">{formatMessage("completed")}</span>
                  )}

                  {dataObject?.BookingType === BookingTypeEnum.NewWeb && (
                    <span className="event-mode bg-danger position-relative">
                      <p className="text-white">New</p>
                    </span>
                  )}

                  <span className="event-mode">
                    {dataObject?.BookingType === BookingTypeEnum.Web || dataObject?.BookingType === BookingTypeEnum.NewWeb ? (
                      <i className="bi bi-globe"></i>
                    ) : dataObject?.BookingType === BookingTypeEnum.Visit || dataObject?.BookingType === BookingTypeEnum.NewVisit ? (
                      <i className="bi bi-shop-window"></i>
                    ) : null}
                  </span>
                </div>

                <div className="treatment-name">
                  <ServiceNameDisplayComponent services={dataObject?.ServiceRequestedList} />
                </div>

                <div className="supporting-details my-2 d-flex align-items-center gap-2">
                  {dataObject?.StaffPreference == 3 && (
                    <span className="event-mode nomination-fee">
                      指
                    </span>
                  )}
                  {dataObject?.StaffPreference == 1 && (
                  <span className="event-mode staff-preference">
                    <img src={demoStoremaleIcon} />
                  </span>
                  )}
                  {dataObject?.StaffPreference == 2 && (
                  <span className="event-mode staff-preference">
                    <img src={demoStorefemaleIcon} />
                  </span>
                  )}
                </div>
              </div>

              <div className="row">
                {(dataObject?.IsMoreEvents || (events?.length <= 2 && shouldShowBreaks && index==1)) && (
                  <div className="col-lg-4 more-events" style={{ cursor: "pointer" }} onClick={openAllMoreEventsPopup}>
                    + {appointmentsCounts !== "0" && `${appointmentsCounts} ${formatMessage("more")}`}
                    {shouldShowBreaks && (
                      <span>
                        {appointmentsCounts !== "0" && ", "} {breaksCount} {breaksCount && breaksCount > 1 ? formatMessage("breaks") : formatMessage("break")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <IntervalEvent
              style={cellWidth} // Pass cell width for interval event
              onResizingChange={onResizingChange} // Pass resizing change handler
              dataObject={dataObject} // Pass event data
              allEvents={allEvents} // Pass all events
              allBreaks={allBreaks} // Pass all breaks
              day={day} // Pass day
              disabled={viewOnly} // Disable if in view-only mode
            />
          </>
        )}
      </DraggableEvent>
    </>
  );
};

// Export the component wrapped with HOCs and observer
export default AllMoreEventsPopupHOC(
  AddBreakDetailsPopupHOC(ReservationDetailsPopupHOC(observer(InProgressEvent)))
);
